import {
  DSBestListRatingsTableColumn,
  DSBestListRatingsTableRow,
  DSBestListRatingsTableCell,
  DSProductSearchResults,
  DSImageProps,
  DSBestListRatingsFacetPriceKey,
} from 'shared-definitions/types'
import { imageMapper } from './imageMapper'
import { PLinkMapperOpts } from './pLinkMapper'
import { wrapWithCurrency } from 'shared-code/format'
import {
  TypesensePrimaryScore,
  TypesenseProductsDocument,
} from 'shared-code/typesense/typesense.types'
import { productTagsMapper } from './productTagsMapper'
import { mapCbPrimaryScoreName } from './mapCbPrimarScoreName'

interface Result {
  columns: DSBestListRatingsTableColumn[]
  rows: DSBestListRatingsTableRow[]
}

interface Opts extends PLinkMapperOpts {
  primaryScore: number
  facetPriceKey: DSBestListRatingsFacetPriceKey
  priceCurrency: string
  strings: {
    productColumn: string
    votesColumn: string
    priceColumn: string
    seeDetails: string
  }
}

/**
 * Maps product search results to a table structure suitable for comparison views.
 *
 * @param input - The product search results containing products and their details.
 * @param opts - Options for mapping, including strings and configuration.
 * @returns An object containing table columns and rows formatted for comparison.
 *
 * @remarks
 * - Each product is mapped to a row with cells for product info, price, votes, and primary scores.
 * - Handles missing data gracefully by providing defaults.
 * - Utilizes helper functions such as `getColumns`, `imageMapper`, and `wrapWithCurrency`.
 */
export function productSearchToCompareColumnsMapper(
  input: DSProductSearchResults,
  opts: Opts
): Result {
  // Define table columns
  const columns: DSBestListRatingsTableColumn[] = getColumns(input, opts)

  // Convert each product to a table row
  const rows: DSBestListRatingsTableRow[] = input.products.map(product => {
    const price = product[`${opts.facetPriceKey}`] ?? 0,
      { priceCurrency } = opts

    const cells: DSBestListRatingsTableCell[] = [
      {
        type: 'product',
        product: {
          id: product.id,
          title: product.name,
          price: wrapWithCurrency(price.toFixed(0), priceCurrency),
          subTitle: product.release_date,
          tags: productTagsMapper(product),
          image: product.image ? imageMapper({ src: product.image }, opts) : null,
          badges:
            (product.badges
              .flatMap(badge => badge.image.map(img => imageMapper(img, opts)))
              .filter(img => img !== null) as DSImageProps[]) || [],
          seeMore: opts.strings.seeDetails,
        },
      },
      {
        type: 'vote',
        id: product.id,
        likes: product.likes,
        dislikes: product.dislikes,
      },
      {
        type: 'numeric',
        value: price,
        label: wrapWithCurrency(price.toFixed(0), priceCurrency),
      },
    ]

    const primaryScoreObj = getPrimaryScore(product, opts.primaryScore)
    if (primaryScoreObj) {
      const psValue = primaryScoreObj.float_casted_to_int
        ? primaryScoreObj.value / 100
        : primaryScoreObj.value
      cells.push({
        type: 'numeric',
        value: psValue,
        label: psValue.toFixed(1),
      })

      primaryScoreObj.core_scores.forEach(coreScore => {
        const value = coreScore.float_casted_to_int ? coreScore.value / 100 : coreScore.value
        cells.push({
          type: 'numeric',
          value,
          label: value.toFixed(1),
        })
      })
    }

    return {
      id: product.id,
      cells,
    }
  })

  return {
    columns,
    rows,
  }
}

function getPrimaryScore(
  product: TypesenseProductsDocument,
  primaryScore: number
): TypesensePrimaryScore | undefined {
  return product.primary_scores.find(item => item.id === primaryScore)
}

/**
 * Generates an array of column definitions for a ratings table based on the provided product search results and options.
 *
 * @param input - The product search results containing products and their associated scores.
 * @param opts - Options specifying which primary score to use for generating additional columns.
 * @returns An array of `DSBestListRatingsTableColumn` objects representing the columns to display in the table.
 */
function getColumns(input: DSProductSearchResults, opts: Opts): DSBestListRatingsTableColumn[] {
  if (input.products.length <= 0) {
    return []
  }

  const columns: DSBestListRatingsTableColumn[] = []
  columns.push({ title: 'Product', columnId: 'product', sortable: false, pinned: true })
  columns.push({
    title: 'Votes',
    columnId: 'votes',
    sortable: false,
    pinned: true,
  })
  columns.push({
    title: 'Price',
    columnId: 'price',
    sortable: true,
    pinned: false,
  })

  const primaryScore = getPrimaryScore(input.products[0], opts.primaryScore)
  if (primaryScore) {
    columns.push({
      title: primaryScore.name,
      columnId: mapCbPrimaryScoreName(primaryScore.id),
      sortable: true,
      pinned: false,
    })

    primaryScore.core_scores.forEach(coreScore => {
      columns.push({
        title: coreScore.name,
        columnId: coreScore.code,
        sortable: true,
        pinned: false,
      })
    })
  }

  return columns
}
